import React, { useMemo } from 'react';
import { AddLeadToGroup } from '@images';

import ActionButton from '@components/action-button/ActionButton';
import AmountInput from '@components/amount-input/AmountInput';
import CurrencyAmount from '@components/currency-amount/CurrencyAmount';
import CustomTable from '@components/custom-table/CustomTable';
import {
  Button,
  ConfirmationModal,
  CountryCode,
  Dropdown,
  EmptyScreen,
  FeeConfigInput,
  Icon,
  Input,
  Label,
  Modal,
  Spinner
} from '@components/index';
import TruncateCell from '@components/truncate-cell/TruncateCell';
import { DEFAULT_PLACEHOLDER } from '@constants/generic';
import CustomTermTable from '@features/group-management/components/v1/custom-term/CustomTermTable';
import { getFeeCategoryDataFromConfig } from '@features/group-management/helper';
import { initialTermsList } from '@features/group-management/helpers/customTerm';
import { addToBranchGroupTableColumn } from '@features/lead-management/constants/leadDetails';
import { getFrequencyValue } from '@features/lead-management/helper';
import useAddToBranchGroup from '@features/lead-management/hooks/useAddToBranchGroup';
import useCurrency, { DEFAULT_CURRENCY_CONFIG } from '@hooks/useCurrency';
import ModalV2 from '@layouts/modal/Modal.v2';
import { formatISODate } from '@utils/date';
import { getNameValidationError } from '@utils/validations';

import LeadGroupDateSettings from './LeadGroupDateSettings';

import theme from '../../../../../themes/colors';

interface AddToBranchGroupTabProps {
  leadId?: string;
}

const AddToBranchGroupTab: React.FC<AddToBranchGroupTabProps> = ({
  leadId
}) => {
  const {
    currencyConfig,
    leadName,
    leadCountryCode,
    leadMobileNumber,
    branchListOptions,
    groupListOptions,
    selectedBranch,
    setSelectedBranch,
    selectedGroup,
    setSelectedGroup,
    groupFormData,
    startDate,
    setStartDate,
    formError,
    setFormError,
    isOpen,
    isEditMode,
    isTableLoading,
    isGroupDetailsLoading,
    isEditDataLoading,
    isSubmitting,
    showDuplicateModal,
    leadBranchGroupDetailsData,
    paginatedBranchGroupData,
    totalCount,
    page,
    setPage,
    handleFormInputChange,
    handleAddModalOpen,
    handleModalClose,
    handleClearGroup,
    handleAddToBranchGroup,
    handleEditClick,
    handleDuplicateModalCancel,
    handleDuplicateModalProceed,
    onAmountChange,
    onAmountBlur,
    updateTermsList,
    editMemberId,
    isBranchOnlyEdit,
    isLeadAddToBranchLoading,
    isLeadAddToGroupLoading,
    isFeeConfigured,
    feeCategories,
    showFeeConfigModal,
    formattedFeeCategories,
    isFeeConfigSaveDisabled,
    hasChangesInFeeConfig,
    handleAmountClick,
    onCategoryAmountChange,
    handleFeeConfigSave,
    handleFeeConfigCancel
  } = useAddToBranchGroup(leadId);

  const { formatCurrencyValue } = useCurrency();

  const isDefaultCurrency =
    currencyConfig?.code === DEFAULT_CURRENCY_CONFIG.code;

  const isGroupDropdownDisabled =
    !selectedBranch || (isEditMode && !!selectedGroup && !isBranchOnlyEdit);

  const nameErrorMessage = useMemo(
    () => getNameValidationError(leadName),
    [leadName]
  );

  const getDisplayAmount = (row: any) => {
    if (
      row?.group_member?.payment_schedule?.interval === 'custom_term' &&
      row?.group_member?.payment_schedule?.current_term_amount
    ) {
      return row.group_member.payment_schedule.current_term_amount;
    }
    return row?.group_member?.amount;
  };

  const modifiedTableData = paginatedBranchGroupData.map((row: any) => ({
    id: `${row?.member?.id}_${row?.group?.id || ''}`,
    branch: (
      <TruncateCell
        id={`${row?.member?.id}_${row?.group?.id || ''}`}
        value={row?.branch?.name || DEFAULT_PLACEHOLDER}
      />
    ),
    groupName: row?.group?.name || DEFAULT_PLACEHOLDER,
    amount: getDisplayAmount(row) ? (
      <div className="flex items-center">
        <CurrencyAmount
          value={getDisplayAmount(row)}
          className={`${isDefaultCurrency ? 'gap-0.25' : 'gap-0.5'}`}
          symbolClass={`${isDefaultCurrency ? 'text-[0.95em]' : ''}`}
          valueClass=""
        />
      </div>
    ) : (
      DEFAULT_PLACEHOLDER
    ),
    recurs: row?.group_member?.payment_schedule?.interval
      ? getFrequencyValue(
          row.group_member.payment_schedule?.interval,
          row.group_member.payment_schedule?.interval_frequency
        )
      : DEFAULT_PLACEHOLDER,
    nextRequestDate: row?.group_member?.next_due_at ? (
      <div className="flex items-center">
        <Icon name="calendar-icon" stroke={theme.cofeeNeutral10} />
        <span className="ml-2 text-cofeeNeutral10">
          {formatISODate({ date: row.group_member.next_due_at })}
        </span>
      </div>
    ) : (
      DEFAULT_PLACEHOLDER
    ),
    actions: (
      <ActionButton
        id={row.member.id}
        handleEdit={(event) =>
          handleEditClick(event, row.member.id, row.group?.id || '')
        }
        isEditDisabled={
          row.group_member?.payment_schedule?.interval === 'once' &&
          row.group_member?.is_collection_started
        }
      />
    )
  }));

  const hasTableData = leadBranchGroupDetailsData.length > 0;

  const renderTableContent = () => {
    if (isTableLoading) {
      return (
        <div className="flex h-150 items-center justify-center">
          <Spinner />
        </div>
      );
    }

    if (!hasTableData) {
      return (
        <div className="h-150">
          <EmptyScreen
            title="Add lead to a group"
            titleStyle="!text-greyText1 !font-medium !text-xl"
            description="Add this lead to an existing group to start managing them with other members."
            descriptionStyle="!mt-1 !text-grey !text-base"
            emptyScreenImage={AddLeadToGroup}
            buttonLabel="Add to group"
            leftIconName="plus"
            leftIconStroke={theme.whiteBorder}
            handleClick={handleAddModalOpen}
            variant="primary"
            className="!mt-4 !justify-start"
          />
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-4">
        <div className="absolute right-6 top-6 z-10 flex justify-end">
          <Button
            variant="secondary"
            className="!justify-start"
            handleClick={handleAddModalOpen}
            label="Add to group"
            labelStyle="!text-cofeePrimary6"
            leftIconName="plus"
            leftIconStroke={theme.cofeePrimary6}
          />
        </div>
        <CustomTable
          headers={addToBranchGroupTableColumn}
          selectedRows={[]}
          handleSelect={() => {}}
          data={modifiedTableData}
          totalCount={totalCount}
          currentPage={page}
          onPageChange={(count: number) => setPage(count)}
          className="!pt-4"
        />
      </div>
    );
  };

  const renderGroupDetails = () => {
    if (!selectedGroup || !groupFormData) return null;

    return (
      <>
        <div className="w-full">
          <Label label="Amount" className="!mb-1 !text-content" isRequired />
          {isFeeConfigured && !groupFormData?.isCustomTerm ? (
            <div
              className="flex h-12.5 w-full cursor-pointer items-center rounded-lg border bg-grey10 px-4 py-2.5 hover:border-primary"
              onClick={handleAmountClick}
              role="presentation"
            >
              <div className="flex h-full items-center justify-center !font-inter">
                {formatCurrencyValue(Number(groupFormData?.amount)).symbol}
              </div>
              <div className="h-9 border border-transparent border-r-grey40 pr-3.5" />
              <div className="pl-3">{groupFormData?.amount || '0'}</div>
            </div>
          ) : (
            <AmountInput
              value={groupFormData?.amount}
              onAmountChange={onAmountChange}
              handleInputBlur={onAmountBlur}
              error={!groupFormData?.isCustomTerm && !!formError?.amount}
              errorMessage={
                !groupFormData?.isCustomTerm ? formError?.amount : ''
              }
              disabled={!!groupFormData?.isCustomTerm || isFeeConfigured}
              className="!bg-theme"
              inputClassNames={`${
                groupFormData?.isCustomTerm || isFeeConfigured
                  ? 'cursor-not-allowed opacity-40'
                  : ''
              }`}
            />
          )}
        </div>
        <LeadGroupDateSettings
          formData={groupFormData}
          groupId={selectedGroup.id as string}
          startDate={startDate}
          setStartDate={setStartDate}
          onInputChange={handleFormInputChange}
          error={formError}
          setError={setFormError}
          isEditGroup={isEditMode}
          memberId={editMemberId || ''}
        />
        {groupFormData.isCustomTerm && (
          <div className="col-span-2">
            <CustomTermTable
              termsList={groupFormData.termsList || [initialTermsList]}
              setTermsList={updateTermsList}
              isFeeCategoryEnabled={isFeeConfigured}
              feeCategories={getFeeCategoryDataFromConfig(feeCategories)}
              onAmountChange={(amount) =>
                handleFormInputChange('amount', amount)
              }
              isMember={isEditMode}
              memberFlow={isEditMode ? 'edit' : 'add'}
              error={formError}
              setError={setFormError}
              groupEndDate={groupFormData.endDate}
            />
          </div>
        )}
      </>
    );
  };

  return (
    <div>
      {renderTableContent()}

      {isOpen && (
        <ModalV2
          title={isEditMode ? 'Edit' : 'Add'}
          onClose={handleModalClose}
          className="!mx-4 w-[1200px] !rounded-2xl bg-white"
          primaryButtonLabel={isEditMode ? 'Update' : 'Add'}
          primaryButtonClick={handleAddToBranchGroup}
          primaryButtonDisabled={
            !selectedBranch ||
            isSubmitting ||
            isEditDataLoading ||
            !!nameErrorMessage
          }
          primaryButtonLoading={isSubmitting || isEditDataLoading}
          secondaryButtonLabel="Cancel"
          secondaryButtonClick={handleModalClose}
          secondaryButtonDisabled={false}
          isClickOutsideRequired={
            !showDuplicateModal &&
            !showFeeConfigModal &&
            !(isFeeConfigured && groupFormData?.isCustomTerm)
          }
          primaryButtonStyle="!w-[85px]"
          secondaryButtonStyle="!w-[85px]"
          bodyClassName="!max-h-[450px] overflow-y-auto no-scrollbar"
        >
          <div className="grid w-full grid-cols-2 gap-4 py-2">
            <div className="gap-4">
              <Label label="Name" isRequired className="!mb-1" />
              <Input
                value={leadName}
                handleInputChange={() => {}}
                type="text"
                placeholder="Name"
                disabled
                className="cursor-not-allowed [&>div>input]:!bg-cofeeGreyBlue100"
                inputStyle={`!text-cofeeNeutral7 ${
                  nameErrorMessage.length > 0 ? '!border-error' : '!border-none'
                }`}
                error={!!nameErrorMessage}
                errorMessage={nameErrorMessage}
              />
            </div>

            <div className="gap-4">
              <Label label="Primary Number" isRequired className="!mb-1" />
              <div className="flex gap-3">
                <CountryCode
                  hasSelection={false}
                  countryCode={leadCountryCode}
                  setCountryCode={() => {}}
                  dropdownStyle="!bg-cofeeGreyBlue100 !border-none"
                  titleLabelStyle="!text-cofeeNeutral7"
                />
                <Input
                  className="w-full cursor-not-allowed"
                  inputStyle="!bg-cofeeGreyBlue100 !border-none !text-cofeeNeutral7"
                  value={leadMobileNumber}
                  handleInputChange={() => {}}
                  placeholder="Enter phone number"
                  type="phone"
                  disabled
                />
              </div>
            </div>

            <div className="gap-4">
              <Label label="Branch" isRequired className="!mb-1" />
              <Dropdown
                filterOptions={branchListOptions}
                searchBarPlaceholder="Search by Branch name"
                currentFilter={selectedBranch?.value || ''}
                onSelectFilter={(option: string) => {
                  const branch = branchListOptions.find(
                    (b) => b.value === option
                  );
                  if (branch) setSelectedBranch(branch);
                }}
                placeholder="Select Branch"
                placeholderStyle="!text-cofeeNeutral6"
                buttonClassName={`w-full h-[50px] ${
                  isEditMode
                    ? '!bg-cofeeGreyBlue100 !border-none'
                    : '!bg-grey10 !bg-white'
                }`}
                optionsStyle="max-h-[200px] overflow-y-auto w-full !bg-white"
                usePortal
                disabled={isEditMode}
                labelStyle="!truncate !min-w-0"
                optionItemClassName="!whitespace-normal break-all"
              />
            </div>

            <div className="gap-4">
              <Label label="Group" className="!mb-1" />
              <Dropdown
                filterOptions={groupListOptions}
                searchBarPlaceholder="Search by Group name"
                currentFilter={selectedGroup?.value || ''}
                onSelectFilter={(option: string) => {
                  const group = groupListOptions.find(
                    (g) => g.value === option
                  );
                  if (group) setSelectedGroup(group);
                }}
                placeholder="Select Group"
                placeholderStyle="!text-cofeeNeutral6"
                optionsStyle="max-h-[200px] overflow-y-auto w-full"
                usePortal
                disabled={isGroupDropdownDisabled}
                buttonClassName={`w-full h-[50px] ${
                  isGroupDropdownDisabled
                    ? '!bg-cofeeGreyBlue100 !border-none'
                    : '!bg-white'
                }`}
                onClear={
                  !isGroupDropdownDisabled ? handleClearGroup : undefined
                }
              />
            </div>

            {selectedGroup && isGroupDetailsLoading && (
              <div className="col-span-2 flex items-center justify-center py-4">
                <Spinner size="lg" />
              </div>
            )}

            {renderGroupDetails()}
          </div>
        </ModalV2>
      )}

      <ConfirmationModal
        isOpen={showDuplicateModal}
        title="Member already exists!"
        message="A member with the same details already exists. If you proceed, the existing record will be overwritten with the new data. Are you sure you want to continue?"
        confirmButtonLabel="Proceed"
        cancelButtonLabel="Cancel"
        handleConfirm={handleDuplicateModalProceed}
        handleCancel={handleDuplicateModalCancel}
        isConfirmButtonLoading={
          isLeadAddToBranchLoading || isLeadAddToGroupLoading
        }
        confirmButtonStyle="text-primary"
        iconName="warning-red"
        className="max-w-118"
      />

      {isFeeConfigured && showFeeConfigModal && (
        <Modal
          isOpen={showFeeConfigModal}
          onCancel={handleFeeConfigCancel}
          isCloseIconRequired
          className="px-6 pt-12"
        >
          <div className="custom-scroll max-h-125 overflow-y-auto pr-6">
            <FeeConfigInput
              feeCategories={formattedFeeCategories}
              onCategoryAmountChange={({ categoryName, value }) => {
                onCategoryAmountChange(categoryName, value);
              }}
              memberActionType={isEditMode ? 'edit' : 'add'}
              isWarningRequired
            />
          </div>
          <div className="mb-3 flex w-full justify-between">
            <Button
              label="Cancel"
              handleClick={(e) => {
                e.stopPropagation();
                handleFeeConfigCancel();
              }}
              variant="secondary"
              size="medium"
              className="w-full"
            />
            <Button
              label="Save"
              handleClick={(e) => {
                e.stopPropagation();
                handleFeeConfigSave();
              }}
              variant="primary"
              size="medium"
              className="ml-6 w-full"
              disabled={isFeeConfigSaveDisabled || !hasChangesInFeeConfig}
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AddToBranchGroupTab;
